/**
 * Session-storage writes that cannot take the page down.
 *
 * ``sessionStorage`` is a shared, fixed budget (~5MB per origin) that many
 * unrelated features write into, and the caches among them — most notably the
 * ``FI_`` file-info cache in ``shared/clientFunctions.ts`` — grow with the
 * number of files an analyst opens and are only ever evicted when that exact
 * key is read again. A long session therefore fills the quota, and the *next*
 * writer of any size is the one that throws, wherever it happens to be. When
 * that writer sat in a React effect (presentation view state) the
 * ``QuotaExceededError`` propagated into the error boundary and replaced the
 * whole app with "Application error: a client-side exception has occurred".
 *
 * Nothing written here is authoritative — it is view state and caches — so a
 * failed write must degrade to "not persisted", never to a crash.
 */

/**
 * Enough of ``Storage`` to write. Reclamation additionally needs to enumerate
 * and read entries, which injected test doubles may not implement, so those
 * members are optional and checked before use.
 */
export type WritableSessionStorage = Pick<Storage, 'setItem'> &
  Partial<Pick<Storage, 'getItem' | 'removeItem' | 'key' | 'length'>>;

/**
 * What a write is worth when the store is full.
 *
 * - ``state`` — UI state with no other home (view state, nav restore point).
 *   Worth evicting cache entries for: they can be fetched again, this cannot.
 * - ``cache`` — a value with a network fallback. Never evicts a live peer;
 *   growing one cache by shrinking another is churn, not progress.
 */
export type SessionWritePriority = 'state' | 'cache';

/**
 * The ``{ value, exp }`` envelope shared by this app's TTL caches. Carrying an
 * ``exp`` is what marks an entry disposable: its owner has already declared a
 * lifetime and must cope with a miss, so eviction only costs a round-trip.
 * Entries without one (view state, nav state, session ids) are never touched.
 */
type CacheEntry = { key: string; exp: number; bytes: number };

/**
 * Live cache a single state write may sacrifice. Effects persist nav and
 * presentation state; without a cap, a hopeless write (quota held by non-TTL
 * keys, or ``setItem`` always rejecting) would walk the whole ``FI_`` cache
 * and empty it. A handful of soonest-to-expire entries is enough to cover a
 * view-state payload; past that the cache is not the thing in the way.
 */
export const MAX_LIVE_CACHE_EVICTIONS = 4;

/** Stores where live eviction already failed to make a write fit. */
const liveEvictionExhausted = new WeakSet<object>();

/**
 * Browsers disagree on how a full store is reported: standard
 * ``QuotaExceededError`` (code 22), Firefox's ``NS_ERROR_DOM_QUOTA_REACHED``
 * (code 1014), and older WebKit's bare code 22 with no useful name.
 */
export function isQuotaExceededError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, code } = error as { name?: unknown; code?: unknown };
  return (
    name === 'QuotaExceededError' ||
    name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    code === 22 ||
    code === 1014
  );
}

/** Every TTL-enveloped entry, or ``null`` when the store cannot be enumerated. */
function collectCacheEntries(storage: WritableSessionStorage): CacheEntry[] | null {
  const { key: keyAt, getItem, removeItem, length } = storage;
  if (
    typeof keyAt !== 'function' ||
    typeof getItem !== 'function' ||
    typeof removeItem !== 'function' ||
    typeof length !== 'number'
  ) {
    return null;
  }

  const entries: CacheEntry[] = [];
  for (let i = 0; i < length; i += 1) {
    const key = keyAt.call(storage, i);
    if (!key) continue;
    const raw = getItem.call(storage, key);
    // Only TTL envelopes are candidates; skip the parse for anything else.
    if (!raw || raw.charCodeAt(0) !== 123 /* { */ || !raw.includes('"exp"')) continue;
    try {
      const parsed = JSON.parse(raw) as { exp?: unknown };
      if (typeof parsed?.exp === 'number') {
        entries.push({ key, exp: parsed.exp, bytes: key.length + raw.length });
      }
    } catch {
      // Not our envelope — leave it for whoever owns it.
    }
  }
  return entries;
}

function removeKeys(storage: WritableSessionStorage, keys: string[]): void {
  for (const key of keys) {
    try {
      storage.removeItem?.(key);
    } catch {
      // A store that refuses removal has nothing left to give.
    }
  }
}

/**
 * Drop entries whose stored ``exp`` timestamp has passed.
 *
 * TTL caches here expire lazily, on a re-read of the same key, so entries for
 * files the analyst never revisits occupy the quota until the tab closes.
 * Sweeping them is free: the data is already stale.
 *
 * Returns the number of entries removed.
 */
export function reclaimExpiredSessionEntries(
  storage: WritableSessionStorage,
  now: number = Date.now(),
): number {
  const expired = (collectCacheEntries(storage) ?? []).filter((entry) => entry.exp <= now);
  removeKeys(storage, expired.map((entry) => entry.key));
  return expired.length;
}

/**
 * Live cache entries, least valuable first — nearest expiry is closest to
 * worthless already, so it is the cheapest thing in the store to lose.
 */
function liveCacheEntriesByValue(
  storage: WritableSessionStorage,
  now: number,
): CacheEntry[] {
  return (collectCacheEntries(storage) ?? [])
    .filter((entry) => entry.exp > now)
    .sort((a, b) => a.exp - b.exp);
}

/**
 * Write ``value``, making room if the store is full. Returns whether the value
 * ended up persisted; callers that only cache may ignore the result.
 *
 * On a quota error it reclaims expired entries and retries, and for a ``state``
 * write it then evicts up to {@link MAX_LIVE_CACHE_EVICTIONS} live cache
 * entries — nearest expiry first — and retries again. Only TTL-enveloped
 * entries are ever removed. The key being written is left in place: native
 * ``setItem`` keeps the previous value when it throws, and wiping it first
 * would lose a restore point the new write then failed to replace.
 *
 * Never throws — that is the entire point.
 */
export function setSessionItem(
  storage: WritableSessionStorage,
  key: string,
  value: string,
  priority: SessionWritePriority = 'state',
): boolean {
  const write = (): boolean | undefined => {
    try {
      storage.setItem(key, value);
      return true;
    } catch (error) {
      if (!isQuotaExceededError(error)) return false;
      return undefined;
    }
  };

  const first = write();
  if (first !== undefined) return first;

  const now = Date.now();
  if (reclaimExpiredSessionEntries(storage, now) > 0) {
    liveEvictionExhausted.delete(storage);
    const afterReclaim = write();
    if (afterReclaim !== undefined) return afterReclaim;
  }

  if (priority !== 'state') return false;
  if (liveEvictionExhausted.has(storage)) return false;

  // How much room is already free is not observable, so rather than guess at a
  // byte target, drop the least valuable cache entries and ask again. Cap the
  // walk: a write that still cannot fit is blocked by something this helper
  // must not touch, and emptying the cache will not change that.
  const live = liveCacheEntriesByValue(storage, now);
  let evicted = 0;
  let batch = 1;
  while (evicted < live.length && evicted < MAX_LIVE_CACHE_EVICTIONS) {
    const take = Math.min(batch, MAX_LIVE_CACHE_EVICTIONS - evicted, live.length - evicted);
    const slice = live.slice(evicted, evicted + take);
    removeKeys(storage, slice.map((entry) => entry.key));
    evicted += slice.length;
    batch *= 2;

    const afterEviction = write();
    if (afterEviction !== undefined) return afterEviction;
  }

  if (evicted > 0) liveEvictionExhausted.add(storage);
  return false;
}
